export const RBC_OUTER_DIAMETER_MICROMETRES = 8.5;
export const RBC_INNER_DIAMETER_MICROMETRES = 6.5;
export const WBC_LPC_ARM_LENGTH_MICROMETRES = 25;
export const WBC_LPC_TICK_INTERVAL_MICROMETRES = 5;

// Retained as a compatibility alias for callers that only need the primary
// RBC calibration size.
export const RULER_DIAMETER_MICROMETRES = RBC_OUTER_DIAMETER_MICROMETRES;
export const RULER_MARGIN_PX = 0;
export const RULER_LABEL_WIDTH_PX = 38;
export const RULER_LABEL_HEIGHT_PX = 16;
export const RULER_LABEL_GAP_PX = 2;
export const RULER_MIN_HIT_TARGET_PX = 24;
export const RULER_FOCUS_RING_EXTENT_PX = 4;
export const RBC_DIMENSION_CLEARANCE_PX = 4;
export const COMPACT_RULER_LABEL_WIDTH_PX = 30;
export const COMPACT_RULER_LABEL_HEIGHT_PX = 9;
export const COMPACT_RULER_LABEL_GAP_PX = 1;

const RULER_GRAPHIC_EDGE_PX = 2;
const RULER_DIMENSION_TICK_PX = 5;
const COMPACT_RULER_DIMENSION_TICK_PX = 3;
const COMPACT_RULER_THRESHOLD_PX = 120;

export type RulerKind = "rbc" | "wbc-lpc";

export type EffectiveMpp = {
  x: number;
  y: number;
};

export type NormalizedRulerPosition = {
  x: number;
  y: number;
};

export type RulerLine = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export type RulerText = {
  x: number;
  y: number;
  rotation: number;
};

export type RulerBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type RulerLayoutBase = {
  kind: RulerKind;
  left: number;
  top: number;
  contentWidth: number;
  contentHeight: number;
  anchorX: number;
  anchorY: number;
  // Compatibility names for the ruler's primary calibrated geometry:
  // the RBC outer diameter or the WBC/LPC horizontal and vertical arms.
  circleWidth: number;
  circleHeight: number;
  availableX: number;
  availableY: number;
  minLeft: number;
  maxLeft: number;
  minTop: number;
  maxTop: number;
  overflowsX: boolean;
  overflowsY: boolean;
  renderedWidth: number;
  renderedHeight: number;
  margin: number;
  marginX: number;
  marginY: number;
  strokeWidth: number;
  labelLeft: number;
  labelTop: number;
  labelWidth: number;
  labelHeight: number;
  labelFontSize: number;
  labelVisible: boolean;
  compact: boolean;
  measurementBounds: RulerBounds;
  hitBounds: RulerBounds;
};

export type RbcRulerGeometry = {
  outerLeft: number;
  outerTop: number;
  outerWidth: number;
  outerHeight: number;
  outerCenterX: number;
  outerCenterY: number;
  innerLeft: number;
  innerTop: number;
  innerWidth: number;
  innerHeight: number;
  innerCenterX: number;
  innerCenterY: number;
  outerDimension: RulerLine;
  outerDimensionStartTick: RulerLine;
  outerDimensionEndTick: RulerLine;
  outerDimensionLabel: RulerText;
  innerDimension: RulerLine;
  innerDimensionStartTick: RulerLine;
  innerDimensionEndTick: RulerLine;
  innerDimensionLabel: RulerText;
  centerHorizontal: RulerLine;
  centerVertical: RulerLine;
  centerPointRadius: number;
};

export type WbcLpcRulerGeometry = {
  originX: number;
  originY: number;
  armWidth: number;
  armHeight: number;
  horizontalArm: RulerLine;
  verticalArm: RulerLine;
  horizontalTicks: RulerLine[];
  verticalTicks: RulerLine[];
  horizontalLabel: RulerText;
  verticalLabel: RulerText;
};

export type RbcRulerLayout = RulerLayoutBase & {
  kind: "rbc";
  rbc: RbcRulerGeometry;
  wbcLpc?: never;
};

export type WbcLpcRulerLayout = RulerLayoutBase & {
  kind: "wbc-lpc";
  rbc?: never;
  wbcLpc: WbcLpcRulerGeometry;
};

export type RulerLayout = RbcRulerLayout | WbcLpcRulerLayout;

export type RulerCssSize = {
  width: number;
  height: number;
};

type RulerGraphicLayout = {
  kind: RulerKind;
  contentWidth: number;
  contentHeight: number;
  anchorX: number;
  anchorY: number;
  primaryWidth: number;
  primaryHeight: number;
  strokeWidth: number;
  labelLeft: number;
  labelTop: number;
  labelWidth: number;
  labelHeight: number;
  labelFontSize: number;
  compact: boolean;
  placementBounds: RulerBounds;
  measurementBounds: RulerBounds;
  rbc?: RbcRulerGeometry;
  wbcLpc?: WbcLpcRulerGeometry;
};

export const DEFAULT_RULER_POSITION: Readonly<NormalizedRulerPosition> = Object.freeze({
  x: 0,
  y: 1,
});

export function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function resolveEffectiveMpp(
  effectiveMppX: unknown,
  effectiveMppY: unknown,
): EffectiveMpp | null {
  if (!isPositiveFiniteNumber(effectiveMppX) || !isPositiveFiniteNumber(effectiveMppY)) {
    return null;
  }
  return { x: effectiveMppX, y: effectiveMppY };
}

export function calculateRulerCssSize(input: {
  effectiveMppX: unknown;
  effectiveMppY: unknown;
  naturalWidth: number;
  naturalHeight: number;
  renderedWidth: number;
  renderedHeight: number;
  diameterMicrometres?: number;
}): RulerCssSize | null {
  const mpp = resolveEffectiveMpp(input.effectiveMppX, input.effectiveMppY);
  const diameter = input.diameterMicrometres ?? RBC_OUTER_DIAMETER_MICROMETRES;
  if (
    !mpp
    || !isPositiveFiniteNumber(input.naturalWidth)
    || !isPositiveFiniteNumber(input.naturalHeight)
    || !isPositiveFiniteNumber(input.renderedWidth)
    || !isPositiveFiniteNumber(input.renderedHeight)
    || !isPositiveFiniteNumber(diameter)
  ) {
    return null;
  }

  const width = (diameter / mpp.x) * (input.renderedWidth / input.naturalWidth);
  const height = (diameter / mpp.y) * (input.renderedHeight / input.naturalHeight);
  if (!isPositiveFiniteNumber(width) || !isPositiveFiniteNumber(height)) return null;
  return { width, height };
}

export function clampNormalizedPosition(
  position: NormalizedRulerPosition,
): NormalizedRulerPosition {
  return {
    x: clamp(position.x, 0, 1),
    y: clamp(position.y, 0, 1),
  };
}

export function calculateRulerLayout(input: {
  kind?: RulerKind;
  effectiveMppX: unknown;
  effectiveMppY: unknown;
  naturalWidth: number;
  naturalHeight: number;
  renderedWidth: number;
  renderedHeight: number;
  position: NormalizedRulerPosition;
  margin?: number;
}): RulerLayout | null {
  const kind = input.kind ?? "rbc";
  const graphic = calculateGraphicLayout(kind, input);
  const requestedMargin = input.margin ?? RULER_MARGIN_PX;
  if (
    graphic == null
    || !Number.isFinite(requestedMargin)
    || requestedMargin < 0
  ) {
    return null;
  }

  const horizontalBounds = resolveAxisPlacementBounds(
    input.renderedWidth,
    graphic.placementBounds.left,
    graphic.placementBounds.width,
    graphic.anchorX,
    requestedMargin,
  );
  const verticalBounds = resolveAxisPlacementBounds(
    input.renderedHeight,
    graphic.placementBounds.top,
    graphic.placementBounds.height,
    graphic.anchorY,
    requestedMargin,
  );
  const marginX = horizontalBounds.margin;
  const marginY = verticalBounds.margin;
  const availableX = horizontalBounds.maximumOffset - horizontalBounds.minimumOffset;
  const availableY = verticalBounds.maximumOffset - verticalBounds.minimumOffset;
  const position = clampNormalizedPosition(input.position);
  const left = clamp(
    (position.x * input.renderedWidth) - graphic.anchorX,
    horizontalBounds.minimumOffset,
    horizontalBounds.maximumOffset,
  );
  const top = clamp(
    (position.y * input.renderedHeight) - graphic.anchorY,
    verticalBounds.minimumOffset,
    verticalBounds.maximumOffset,
  );
  const hitBounds = calculateHitBounds(
    input.renderedWidth,
    input.renderedHeight,
    left,
    top,
    graphic.anchorX,
    graphic.anchorY,
    graphic.measurementBounds,
  );

  const base: RulerLayoutBase = {
    kind,
    left,
    top,
    contentWidth: graphic.contentWidth,
    contentHeight: graphic.contentHeight,
    anchorX: graphic.anchorX,
    anchorY: graphic.anchorY,
    circleWidth: graphic.primaryWidth,
    circleHeight: graphic.primaryHeight,
    availableX,
    availableY,
    minLeft: horizontalBounds.minimumOffset,
    maxLeft: horizontalBounds.maximumOffset,
    minTop: verticalBounds.minimumOffset,
    maxTop: verticalBounds.maximumOffset,
    overflowsX: left < 0 || left + graphic.contentWidth > input.renderedWidth,
    overflowsY: top < 0 || top + graphic.contentHeight > input.renderedHeight,
    renderedWidth: input.renderedWidth,
    renderedHeight: input.renderedHeight,
    margin: Math.max(marginX, marginY),
    marginX,
    marginY,
    strokeWidth: graphic.strokeWidth,
    labelLeft: graphic.labelLeft,
    labelTop: graphic.labelTop,
    labelWidth: graphic.labelWidth,
    labelHeight: graphic.labelHeight,
    labelFontSize: graphic.labelFontSize,
    labelVisible: true,
    compact: graphic.compact,
    measurementBounds: graphic.measurementBounds,
    hitBounds,
  };

  if (kind === "rbc" && graphic.rbc) {
    return { ...base, kind, rbc: graphic.rbc };
  }
  if (kind === "wbc-lpc" && graphic.wbcLpc) {
    return { ...base, kind, wbcLpc: graphic.wbcLpc };
  }
  return null;
}

export function normalizedPositionFromTopLeft(
  left: number,
  top: number,
  layout: RulerLayout,
  previousPosition?: NormalizedRulerPosition,
): NormalizedRulerPosition {
  const {
    minLeft,
    maxLeft,
    minTop,
    maxTop,
  } = layout;
  const clampedLeft = clamp(left, minLeft, maxLeft);
  const clampedTop = clamp(top, minTop, maxTop);
  const normalized = {
    x: normalizedAxisFromOffset(
      left,
      clampedLeft,
      minLeft,
      maxLeft,
      layout.anchorX,
      layout.renderedWidth,
      previousPosition?.x,
    ),
    y: normalizedAxisFromOffset(
      top,
      clampedTop,
      minTop,
      maxTop,
      layout.anchorY,
      layout.renderedHeight,
      previousPosition?.y,
    ),
  };
  return clampNormalizedPosition(normalized);
}

function resolveAxisPlacementBounds(
  renderedSize: number,
  placementOffset: number,
  placementSize: number,
  anchorOffset: number,
  requestedMargin: number,
) {
  const maximumContainedMargin = (renderedSize - placementSize) / 2;
  if (maximumContainedMargin >= 0) {
    const margin = Math.min(requestedMargin, maximumContainedMargin);
    return {
      minimumOffset: margin - placementOffset,
      maximumOffset: renderedSize - margin - placementOffset - placementSize,
      margin,
    };
  }

  // At extreme zoom, keep the anchor inside the image when even the placement
  // geometry cannot fit.
  const anchorMargin = Math.min(requestedMargin, renderedSize / 2);
  return {
    minimumOffset: anchorMargin - anchorOffset,
    maximumOffset: renderedSize - anchorMargin - anchorOffset,
    margin: anchorMargin,
  };
}

function calculateHitBounds(
  renderedWidth: number,
  renderedHeight: number,
  left: number,
  top: number,
  anchorX: number,
  anchorY: number,
  measurementBounds: RulerBounds,
): RulerBounds {
  const horizontal = resolveHitAxis(
    renderedWidth,
    left,
    anchorX,
    measurementBounds.left,
    measurementBounds.width,
  );
  const vertical = resolveHitAxis(
    renderedHeight,
    top,
    anchorY,
    measurementBounds.top,
    measurementBounds.height,
  );
  return {
    left: horizontal.offset,
    top: vertical.offset,
    width: horizontal.size,
    height: vertical.size,
  };
}

function resolveHitAxis(
  renderedSize: number,
  wrapperOffset: number,
  anchorOffset: number,
  measurementOffset: number,
  measurementSize: number,
) {
  const measurementStart = wrapperOffset + measurementOffset;
  const measurementEnd = measurementStart + measurementSize;
  const visibleStart = clamp(measurementStart, 0, renderedSize);
  const visibleEnd = clamp(measurementEnd, 0, renderedSize);
  const visibleSize = Math.max(0, visibleEnd - visibleStart);
  const geometryFits = measurementSize <= renderedSize;
  const hittableGeometrySize = geometryFits ? visibleSize : 0;
  const size = Math.min(
    renderedSize,
    Math.max(hittableGeometrySize, RULER_MIN_HIT_TARGET_PX),
  );
  const center = geometryFits && visibleSize >= RULER_MIN_HIT_TARGET_PX
    ? visibleStart + (visibleSize / 2)
    : wrapperOffset + anchorOffset;
  const globalOffset = clamp(
    center - (size / 2),
    0,
    renderedSize - size,
  );
  return {
    offset: globalOffset - wrapperOffset,
    size,
  };
}

function normalizedAxisFromOffset(
  requestedOffset: number,
  clampedOffset: number,
  minimumOffset: number,
  maximumOffset: number,
  anchorOffset: number,
  renderedSize: number,
  previousPosition?: number,
): number {
  if (maximumOffset <= minimumOffset) {
    if (requestedOffset < minimumOffset) return 0;
    if (requestedOffset > maximumOffset) return 1;
    if (previousPosition !== undefined) return previousPosition;
  }
  if (clampedOffset === minimumOffset) return 0;
  if (clampedOffset === maximumOffset) return 1;
  return (clampedOffset + anchorOffset) / renderedSize;
}

function calculateGraphicLayout(
  kind: RulerKind,
  input: Parameters<typeof calculateRulerLayout>[0],
): RulerGraphicLayout | null {
  if (kind === "rbc") return calculateRbcGraphicLayout(input);
  if (kind === "wbc-lpc") return calculateWbcLpcGraphicLayout(input);
  return null;
}

function calculateRbcGraphicLayout(
  input: Parameters<typeof calculateRulerLayout>[0],
): RulerGraphicLayout | null {
  const outer = calculateRulerCssSize({
    ...input,
    diameterMicrometres: RBC_OUTER_DIAMETER_MICROMETRES,
  });
  const inner = calculateRulerCssSize({
    ...input,
    diameterMicrometres: RBC_INNER_DIAMETER_MICROMETRES,
  });
  if (!outer || !inner) return null;

  const presentation = resolvePresentation(input.renderedWidth, input.renderedHeight);
  const {
    compact,
    labelWidth,
    labelHeight,
    labelGap,
    labelFontSize,
    tickLength,
  } = presentation;
  const strokeWidth = calculateStrokeWidth(outer.width, outer.height);
  const horizontalLabelOverhang = Math.max(0, (labelWidth - outer.width) / 2);
  const verticalLabelOverhang = Math.max(0, (labelWidth - outer.height) / 2);
  const dimensionPadding = (
    labelHeight
    + tickLength
    + labelGap
    + RBC_DIMENSION_CLEARANCE_PX
  );
  const outerLeft = RULER_GRAPHIC_EDGE_PX + dimensionPadding + horizontalLabelOverhang;
  const outerTop = RULER_GRAPHIC_EDGE_PX + dimensionPadding + verticalLabelOverhang;
  const outerCenterX = outerLeft + (outer.width / 2);
  const outerCenterY = outerTop + (outer.height / 2);
  const innerLeft = outerCenterX - (inner.width / 2);
  const innerTop = outerCenterY - (inner.height / 2);
  const contentWidth = (
    (RULER_GRAPHIC_EDGE_PX * 2)
    + dimensionPadding
    + Math.max(outer.width, labelWidth)
  );
  const contentHeight = (
    (RULER_GRAPHIC_EDGE_PX * 2)
    + dimensionPadding
    + Math.max(outer.height, labelWidth)
  );

  const outerDimensionY = (
    outerTop
    - RBC_DIMENSION_CLEARANCE_PX
    - (tickLength / 2)
  );
  const innerDimensionX = (
    outerLeft
    - RBC_DIMENSION_CLEARANCE_PX
    - (tickLength / 2)
  );
  const centerMarkRadius = Math.max(
    1,
    Math.min(6, outer.width / 4, outer.height / 4),
  );
  const centerPointRadius = Math.max(
    0.75,
    Math.min(2, outer.width / 10, outer.height / 10),
  );

  const rbc: RbcRulerGeometry = {
    outerLeft,
    outerTop,
    outerWidth: outer.width,
    outerHeight: outer.height,
    outerCenterX,
    outerCenterY,
    innerLeft,
    innerTop,
    innerWidth: inner.width,
    innerHeight: inner.height,
    innerCenterX: outerCenterX,
    innerCenterY: outerCenterY,
    outerDimension: line(outerLeft, outerDimensionY, outerLeft + outer.width, outerDimensionY),
    outerDimensionStartTick: line(
      outerLeft,
      outerDimensionY - (tickLength / 2),
      outerLeft,
      outerDimensionY + (tickLength / 2),
    ),
    outerDimensionEndTick: line(
      outerLeft + outer.width,
      outerDimensionY - (tickLength / 2),
      outerLeft + outer.width,
      outerDimensionY + (tickLength / 2),
    ),
    outerDimensionLabel: {
      x: outerCenterX,
      y: outerDimensionY - (tickLength / 2) - labelGap - (labelHeight / 2),
      rotation: 0,
    },
    innerDimension: line(
      innerDimensionX,
      innerTop,
      innerDimensionX,
      innerTop + inner.height,
    ),
    innerDimensionStartTick: line(
      innerDimensionX - (tickLength / 2),
      innerTop,
      innerDimensionX + (tickLength / 2),
      innerTop,
    ),
    innerDimensionEndTick: line(
      innerDimensionX - (tickLength / 2),
      innerTop + inner.height,
      innerDimensionX + (tickLength / 2),
      innerTop + inner.height,
    ),
    innerDimensionLabel: {
      x: innerDimensionX - (tickLength / 2) - labelGap - (labelHeight / 2),
      y: outerCenterY,
      rotation: -90,
    },
    centerHorizontal: line(
      outerCenterX - centerMarkRadius,
      outerCenterY,
      outerCenterX + centerMarkRadius,
      outerCenterY,
    ),
    centerVertical: line(
      outerCenterX,
      outerCenterY - centerMarkRadius,
      outerCenterX,
      outerCenterY + centerMarkRadius,
    ),
    centerPointRadius,
  };

  return {
    kind: "rbc",
    contentWidth,
    contentHeight,
    anchorX: outerCenterX,
    anchorY: outerCenterY,
    primaryWidth: outer.width,
    primaryHeight: outer.height,
    strokeWidth,
    labelLeft: rbc.outerDimensionLabel.x - (labelWidth / 2),
    labelTop: rbc.outerDimensionLabel.y - (labelHeight / 2),
    labelWidth,
    labelHeight,
    labelFontSize,
    compact,
    placementBounds: {
      left: innerLeft,
      top: innerTop,
      width: inner.width,
      height: inner.height,
    },
    measurementBounds: {
      left: outerLeft,
      top: outerTop,
      width: outer.width,
      height: outer.height,
    },
    rbc,
  };
}

function calculateWbcLpcGraphicLayout(
  input: Parameters<typeof calculateRulerLayout>[0],
): RulerGraphicLayout | null {
  const arms = calculateRulerCssSize({
    ...input,
    diameterMicrometres: WBC_LPC_ARM_LENGTH_MICROMETRES,
  });
  if (!arms) return null;

  const presentation = resolvePresentation(input.renderedWidth, input.renderedHeight);
  const {
    compact,
    labelWidth,
    labelHeight,
    labelGap,
    labelFontSize,
    tickLength,
  } = presentation;
  const strokeWidth = calculateStrokeWidth(arms.width, arms.height);
  const horizontalBodyWidth = Math.max(arms.width, tickLength);
  const horizontalLabelOverhang = Math.max(0, (labelWidth - horizontalBodyWidth) / 2);
  const verticalLabelOverhang = Math.max(0, (labelWidth - arms.height) / 2);
  const leftPadding = labelHeight + labelGap;
  const bottomPadding = labelHeight + labelGap;
  const originX = RULER_GRAPHIC_EDGE_PX + leftPadding + horizontalLabelOverhang;
  const originY = RULER_GRAPHIC_EDGE_PX + verticalLabelOverhang + arms.height;
  const contentWidth = (
    (RULER_GRAPHIC_EDGE_PX * 2)
    + leftPadding
    + Math.max(horizontalBodyWidth, labelWidth)
  );
  const contentHeight = (
    (RULER_GRAPHIC_EDGE_PX * 2)
    + Math.max(arms.height, labelWidth)
    + bottomPadding
  );
  const tickCount = WBC_LPC_ARM_LENGTH_MICROMETRES / WBC_LPC_TICK_INTERVAL_MICROMETRES;
  const horizontalTicks = Array.from({ length: tickCount }, (_, index) => {
    const x = originX + (arms.width * ((index + 1) / tickCount));
    return line(x, originY, x, originY - tickLength);
  });
  const verticalTicks = Array.from({ length: tickCount }, (_, index) => {
    const y = originY - (arms.height * ((index + 1) / tickCount));
    return line(originX, y, originX + tickLength, y);
  });

  const wbcLpc: WbcLpcRulerGeometry = {
    originX,
    originY,
    armWidth: arms.width,
    armHeight: arms.height,
    horizontalArm: line(originX, originY, originX + arms.width, originY),
    verticalArm: line(originX, originY, originX, originY - arms.height),
    horizontalTicks,
    verticalTicks,
    horizontalLabel: {
      x: originX + (arms.width / 2),
      y: originY + labelGap + (labelHeight / 2),
      rotation: 0,
    },
    verticalLabel: {
      x: originX - labelGap - (labelHeight / 2),
      y: originY - (arms.height / 2),
      rotation: -90,
    },
  };

  return {
    kind: "wbc-lpc",
    contentWidth,
    contentHeight,
    anchorX: originX,
    anchorY: originY,
    primaryWidth: arms.width,
    primaryHeight: arms.height,
    strokeWidth,
    labelLeft: wbcLpc.horizontalLabel.x - (labelWidth / 2),
    labelTop: wbcLpc.horizontalLabel.y - (labelHeight / 2),
    labelWidth,
    labelHeight,
    labelFontSize,
    compact,
    // placementBounds semantics differ by kind: rbc clamps by its inner circle,
    // wbc-lpc by the L corner alone. The corner is the measuring point, so it
    // must reach every pixel of the image including the top-right; the arms are
    // allowed to run off the edge there.
    placementBounds: {
      left: originX,
      top: originY,
      width: 0,
      height: 0,
    },
    measurementBounds: {
      left: originX,
      top: originY - arms.height,
      width: arms.width,
      height: arms.height,
    },
    wbcLpc,
  };
}

function resolvePresentation(renderedWidth: number, renderedHeight: number) {
  const compact = (
    renderedWidth < COMPACT_RULER_THRESHOLD_PX
    || renderedHeight < COMPACT_RULER_THRESHOLD_PX
  );
  return {
    compact,
    labelWidth: compact ? COMPACT_RULER_LABEL_WIDTH_PX : RULER_LABEL_WIDTH_PX,
    labelHeight: compact ? COMPACT_RULER_LABEL_HEIGHT_PX : RULER_LABEL_HEIGHT_PX,
    labelGap: compact ? COMPACT_RULER_LABEL_GAP_PX : RULER_LABEL_GAP_PX,
    labelFontSize: compact ? 8 : 11,
    tickLength: compact ? COMPACT_RULER_DIMENSION_TICK_PX : RULER_DIMENSION_TICK_PX,
  };
}

function calculateStrokeWidth(width: number, height: number): number {
  return Math.min(1.5, width / 3, height / 3);
}

function line(x1: number, y1: number, x2: number, y2: number): RulerLine {
  return { x1, y1, x2, y2 };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}
